import type { FilePublic } from '@repo/types/files';
import type { PostMutationOptions, PostPublic } from '@repo/types/posts';
import type { Editor } from '@tiptap/react';

import { cn } from '@repo/lib/cn';
import { FileTypeUtils } from '@repo/lib/fs/fileTypeUtils';
import { getBase64 } from '@repo/lib/fs/getBase64';
import { getFileExtension } from '@repo/lib/fs/getFileExtension';
import { FileType } from '@repo/types/enums/fileType';
import { formatDistance, parseISO } from 'date-fns';
import { ChevronLeft, ChevronRight, DownloadIcon, EyeIcon, Minus, MoreVertical, PenLine, Plus, TimerIcon, Trash2, User } from 'lucide-react';
import { startTransition, useCallback, useEffect, useMemo, useState } from 'react';

import type { CarouselApi } from '@/components/ui/carousel';

import ActivatePost from '@/components/features/manage/manage-posts/ActivatePost';
import DeletePost from '@/components/features/manage/manage-posts/DeletePost';
import EditPost from '@/components/features/manage/manage-posts/EditPost';
import PostReadReceiptsDialog from '@/components/features/post/PostReadReceiptsDialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { FileIcon } from '@/components/ui/file-icon';
import { FileCard, FileCardContent, FileCardTitle } from '@/components/ui/fs-card';
import { Image } from '@/components/ui/image';
import { AudioPlayer, VideoPlayer } from '@/components/ui/media-player';
import { Tiptap } from '@/components/ui/tiptap-editor';
import { postExtensionSet } from '@/lib/tiptap-extensions-sets';
import { useCredentials } from '@/store/auth-store';
import { getFileUrl } from '@/utils/file/getFileUrl';
import { getAvatar } from '@/utils/getAvatar/getAvatar';

/**
 * Props for the PostCard component.
 * @property {string} [className] - Optional additional class names.
 * @property {PostPublic} post - The post data to display.
 * @property {PostMutationOptions} [options] - The options for the post mutation.
 * @property {() => void} [onDeleted] - The function to call when the post is deleted.
 */
interface PostProps {
  className?: string;
  post: PostPublic;
  options?: PostMutationOptions;
  onDeleted?: () => void;
}

export default function PostCard(postProps: PostProps) {
  const { className, post, options, onDeleted } = postProps;
  const { lvl } = useCredentials();
  const isAdmin: boolean = lvl === 2;
  const isStaff: boolean = lvl > 0;
  const [postMedia, postFiles] = useMemo(() => {
    const media: PostCardMediaProps[] = [];
    const files: FilePublic[] = [];
    if (post.files.length > 0) {
      post.files.forEach((file) => {
        const { serveType, mimeType } = FileTypeUtils.getFileType(file, {
          pick: [FileType.IMAGE, FileType.AUDIO, FileType.VIDEO],
        });
        if (serveType === FileType.FILE) {
          files.push(file);
        } else {
          media.push({
            file: file,
            serveType: serveType,
            mimeType: mimeType,
          });
        }
      });
    }
    return [media, files];
  }, [post]);
  const isPostFiles: boolean = postFiles.length > 0;
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openChangeActive, setOpenChangeActive] = useState<boolean>(false);
  const [openReadReceipts, setOpenReadReceipts] = useState<boolean>(false);
  const [editor, setEditor] = useState<Editor | null>(null);

  useEffect(() => {
    if (editor && post.content) {
      editor.commands.setContent(post.content);
    }
  }, [post.content, editor]);

  const downloadFile = (file: FilePublic) => {
    const url = getFileUrl(file.id);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', file.name);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const calculatePassedTime = useCallback(() => {
    const date = parseISO(post.publishedAt);
    const passedTime = formatDistance(date, new Date(), {
      addSuffix: true,
    });

    return {
      text: date > new Date() ? `Dostępny ${passedTime}` : passedTime,
      isFuture: date > new Date(),
    };
  }, [post.publishedAt]);

  const [passedTime, setPassedTime] = useState(calculatePassedTime);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleUpdate = () => {
      timeoutId = setTimeout(() => {
        startTransition(() => {
          setPassedTime(calculatePassedTime());
        });
        scheduleUpdate();
      }, 60 * 1000);
    };

    scheduleUpdate();

    return () => {
      clearTimeout(timeoutId);
    };
  }, [calculatePassedTime]);

  return (
    <>
      {isAdmin ? (
        <>
          <EditPost open={openEdit} onOpenChange={setOpenEdit} post={post} options={options} />
          <ActivatePost open={openChangeActive} onOpenChange={setOpenChangeActive} post={post} options={options} />
          <DeletePost posts={[post]} open={openDelete} onOpenChange={setOpenDelete} onDeleted={onDeleted} options={options} />
        </>
      ) : null}
      {isStaff ? <PostReadReceiptsDialog postId={post.id} open={openReadReceipts} onOpenChange={setOpenReadReceipts} /> : null}

      <div aria-disabled={!post.active} className={cn('bg-background w-full rounded-2xl border aria-disabled:opacity-75', className)}>
        <header className={'flex items-center gap-2 border-b p-4'}>
          <Avatar size={'md'}>
            <AvatarImage src={getAvatar(post.creator.avatarURL)} alt="avatar" />
            <AvatarFallback>
              <User />
            </AvatarFallback>
          </Avatar>
          <div className={'flex w-full grow flex-col'}>
            <h3 className={'text-md text-foreground font-semibold'}>
              {post.creator.name} {post.creator.surname}
            </h3>
            <p className={cn('flex items-center gap-1 text-xs', passedTime.isFuture ? 'text-warning font-medium' : 'text-muted-foreground')}>
              {passedTime.isFuture && <TimerIcon className="size-3.5" />}
              {passedTime.text}
            </p>
          </div>
          {isStaff ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size={'sm'} variant={'ghost'} aria-label={'Opcje posta'}>
                  <MoreVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={'end'}>
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={() => setOpenReadReceipts(true)}>
                    <EyeIcon />
                    Odczytane
                  </DropdownMenuItem>
                  {isAdmin ? (
                    <DropdownMenuItem onClick={() => setOpenEdit(true)}>
                      <PenLine />
                      Edytuj
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuGroup>
                {isAdmin ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                      <DropdownMenuItem variant={post.active ? 'destructive' : 'default'} onClick={() => setOpenChangeActive(true)}>
                        {post.active ? (
                          <>
                            <Minus />
                            Dezaktywuj
                          </>
                        ) : (
                          <>
                            <Plus />
                            Aktywuj
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setOpenDelete(true)} variant={'destructive'}>
                        <Trash2 />
                        Usuń
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </header>
        <div className="space-y-4 p-4">
          <h5 className="text-foreground text-2xl font-semibold empty:hidden">{post.name}</h5>
          <Tiptap extensions={postExtensionSet} editable={false} content={post.content} onEditorReady={setEditor} />
          <PostCardCarousel media={postMedia} />
          {isPostFiles ? (
            <div className={'flex flex-col gap-2'}>
              <p className={'text-muted-foreground text-xs'}>Załączniki:</p>
              <div className={'grid-auto-flow grid gap-2'}>
                {postFiles.map((file) => (
                  <FileCard key={file.id}>
                    <DropdownMenu>
                      <FileCardContent>
                        <FileIcon fileExtension={getFileExtension(file.name)} />
                        <FileCardTitle>{file.name}</FileCardTitle>
                        <DropdownMenuTrigger asChild>
                          <Button variant={'ghost'} size={'sm'}>
                            <MoreVertical />
                          </Button>
                        </DropdownMenuTrigger>
                      </FileCardContent>
                      <DropdownMenuContent align={'end'}>
                        <DropdownMenuGroup>
                          <DropdownMenuItem onClick={() => downloadFile(file)}>
                            <DownloadIcon className={'size-4'} />
                            Pobierz
                          </DropdownMenuItem>
                        </DropdownMenuGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </FileCard>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

/**
 * Props for the PostCardCarousel component.
 * @property {PostCardMediaProps[]} media - Array of media files to display.
 */
interface PostCardCarouselProps {
  media: PostCardMediaProps[];
}

function PostCardCarousel(postCardMediaCarousel: PostCardCarouselProps) {
  const { media } = postCardMediaCarousel;
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState<number>(0);
  const [count, setCount] = useState<number>(0);
  const { isEmpty, isOne } = useMemo(() => {
    return {
      isEmpty: media.length === 0,
      isOne: media.length === 1,
    };
  }, [media.length]);

  useEffect(() => {
    if (!api) {
      return;
    }
    setCount(media.length);
    const selectedIndex = api.selectedScrollSnap();

    if (selectedIndex >= media.length && media.length > 0) {
      api.scrollTo(media.length - 1);
      setCurrent(media.length);
    } else {
      setCurrent(selectedIndex + 1);
    }
  }, [api, media.length]);

  useEffect(() => {
    if (!api) {
      return;
    }
    const setCurrentOnSelect = () => {
      const selectedIndex = api.selectedScrollSnap();

      if (selectedIndex >= media.length && media.length > 0) {
        api.scrollTo(media.length - 1);
        setCurrent(media.length);
      } else {
        setCurrent(selectedIndex + 1);
      }
    };

    api.on('select', setCurrentOnSelect);

    return () => {
      api.off('select', setCurrentOnSelect);
    };
  }, [api, media.length]);

  if (isEmpty) {
    return null;
  }

  return (
    <Carousel setApi={setApi} className={'relative w-full'}>
      <span
        className={cn(
          'bg-background media-buffering:text-muted-foreground absolute top-2 right-2 z-40 flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium tracking-wide',
          isOne && 'hidden'
        )}
      >
        {current}/{count}
      </span>
      <CarouselContent>
        {media.map((mediaProps) => (
          <CarouselItem key={mediaProps.file.id}>
            <PostCardMedia file={mediaProps.file} serveType={mediaProps.serveType} mimeType={mediaProps.mimeType} />
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselPrevious variant={'tertiary'} size={'sm'} className={cn('h-8 min-h-8 w-8 min-w-8', isOne && 'hidden')}>
        <ChevronLeft />
      </CarouselPrevious>
      <CarouselNext variant={'tertiary'} size={'sm'} className={cn('h-8 min-h-8 w-8 min-w-8', isOne && 'hidden')}>
        <ChevronRight />
      </CarouselNext>
    </Carousel>
  );
}

/**
 * Props for the PostCardMedia component.
 * @property {FilePublic} file - The media file to display.
 * @property {FileType} serveType - The type of media (image, audio, video).
 * @property {string} mimeType - The MIME type of the media.
 */
interface PostCardMediaProps {
  file: FilePublic;
  serveType: FileType;
  mimeType: string;
}
function PostCardMedia(postCardMediaProps: PostCardMediaProps) {
  const { file, mimeType, serveType } = postCardMediaProps;
  const { id, name, fileType: type, thumbnail } = file;

  const base64 = getBase64({
    base64: thumbnail ?? undefined,
    fileType: type,
    clear: true,
  });

  const content = useMemo(() => {
    switch (serveType) {
      case FileType.IMAGE:
        return <Image src={getFileUrl(id)} alt={name} fill placeholder={'blur'} blurDataURL={base64} loading={'lazy'} />;
      case FileType.VIDEO:
        return <VideoPlayer src={getFileUrl(id)} title={name} size={'lg'} />;
      case FileType.AUDIO:
        return (
          <div className="flex w-full items-center justify-center px-13">
            <AudioPlayer src={getFileUrl(id)} className="rounded-md border p-2" />
          </div>
        );
    }
  }, [serveType, id, name, thumbnail, type, mimeType]);

  return <div className={'relative flex h-[40vh] max-h-120 min-h-50 justify-center gap-2 overflow-hidden rounded-md'}>{content}</div>;
}
